/**
 * Data layer for `custom-fraud-rule-engine-per-campaign-per-partner`.
 * Thin typed fetch wrappers over /api/v1/custom-fraud-rule-engine-per-campaign-per-partner.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Modelled after the pre-nuke "Per-partner fraud slice" report (legacy path
 * apps/dash-legacy/src/routes/_authed/fraud/reports/per-partner.tsx): a row
 * per affiliate with conversion counts, flagged counts, fraud rate and
 * blocked-clicks total, plus a rule-level create/delete surface for the
 * campaign/partner-scoped fraud-rule engine this feature exposes.
 */

export interface PerPartnerFraudRow {
	affiliate_id: string;
	total_conversions: number;
	flagged_conversions: number;
	fraud_rate: number;
	blocked_clicks: number;
}

export interface CustomFraudRuleEnginePerCampaignPerPartnerRow {
	id: string;
	tenant_id: string;
	campaign_id?: string | null;
	partner_id?: string | null;
	name?: string | null;
	rule?: string | null;
	created_at: number;
	updated_at: number;
	rows?: PerPartnerFraudRow[];
	[key: string]: unknown;
}

export interface ListCustomFraudRuleEnginePerCampaignPerPartnerResponse {
	count: number;
	items: CustomFraudRuleEnginePerCampaignPerPartnerRow[];
	rows?: PerPartnerFraudRow[];
}

export interface CreateCustomFraudRuleEnginePerCampaignPerPartnerBody {
	name?: string;
	campaign_id?: string;
	partner_id?: string;
	rule?: string;
	[key: string]: unknown;
}

const BASE = "/api/v1/custom-fraud-rule-engine-per-campaign-per-partner";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	period?: string,
): Promise<ListCustomFraudRuleEnginePerCampaignPerPartnerResponse> {
	const url = period ? `${BASE}?period=${encodeURIComponent(period)}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomFraudRuleEnginePerCampaignPerPartnerResponse>(res);
}

export async function create(
	body: CreateCustomFraudRuleEnginePerCampaignPerPartnerBody,
): Promise<CustomFraudRuleEnginePerCampaignPerPartnerRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomFraudRuleEnginePerCampaignPerPartnerRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customFraudRuleEnginePerCampaignPerPartnerQueryKeys = {
	all: ["custom-fraud-rule-engine-per-campaign-per-partner"] as const,
	list: (period?: string) =>
		[
			...customFraudRuleEnginePerCampaignPerPartnerQueryKeys.all,
			"list",
			period ?? "all",
		] as const,
	detail: (id: string) =>
		[...customFraudRuleEnginePerCampaignPerPartnerQueryKeys.all, id] as const,
};
